import { createAction } from '@reduxjs/toolkit';
import { isToday } from 'date-fns';
import { isEmpty } from 'ramda';
import {
  CHANGE_ADDITIONAL_SERVICE_POPUP_TYPE,
  CHECK_SERVICE_ADDITIONAL_USER_AGREEMENT,
  ERROR_MESSAGES,
  SERVICE_CONNECTION_CODES,
  SET_ADDITIONAL_SERVICE_EMAIL,
  SET_ADDITIONAL_SERVICE_POPUP_VISIBLE,
  SET_SERVICE_ADDITIONAL_CONNECTION_DATA,
  SET_SERVICE_ADDITIONAL_POPUP_DATA,
  SET_SERVICE_WITH_VALUES,
} from '../../constants';
import {
  getFTTBServiceFutureRequestId,
  getFTTBServiceId, getFTTBServiceSplId,
  getFTTBTariffPresetId,
  getFTTBTariffPresetSplId,
} from '../selectors';
import {
  cancelFutureTaskRequest,
  connectAdditionalServicePromisedPaymentRequest,
  connectAdditionalServiceRequest,
  disableServiceCheckRequest,
  disableServiceRequest,
  getPopupAdditionalServiceRequest,
} from '../../api/requests';
import { fetchDataError } from './commonActions';
import {
  getFTTBAdditionalPopup,
  getFTTBAdditionalPopupConnection,
  getFTTBServiceRequestDisconnectionDate,
} from '../selectors/servicesSelectors';

export const setServiceAdditionalPopupData = createAction(SET_SERVICE_ADDITIONAL_POPUP_DATA);
export const setFTTBServiceValuesAction = createAction(SET_SERVICE_WITH_VALUES);
export const changeServiceAdditionalPopupType = createAction(CHANGE_ADDITIONAL_SERVICE_POPUP_TYPE);
export const setServiceAdditionalConnectionEmail = createAction(SET_ADDITIONAL_SERVICE_EMAIL);
export const checkServiceAdditionalUserAgreement = createAction(CHECK_SERVICE_ADDITIONAL_USER_AGREEMENT);
export const switchServiceAdditionalPopup = createAction(SET_ADDITIONAL_SERVICE_POPUP_VISIBLE);
export const fetchServiceAdditionalConnection = createAction(SET_SERVICE_ADDITIONAL_CONNECTION_DATA);

export const fetchDisconnectionParams = (initialParams = null) => (dispatch, getState) => {
  if (initialParams) return;

  const serviceId = getFTTBServiceId(getState());
  const splId = getFTTBServiceSplId(getState());
  const presetId = getFTTBTariffPresetId(getState());

  disableServiceCheckRequest(splId, presetId)
    .then(({ data }) => {
      if (data.isProcessing) {
        dispatch(setFTTBServiceValuesAction({ serviceId, error: ERROR_MESSAGES.PREV_REQUEST_IN_PROGRESS_MESSAGE }));
        return;
      }
      dispatch(setFTTBServiceValuesAction({ serviceId, disconnectionParams: data }));
    }).catch(() => dispatch(setFTTBServiceValuesAction({ serviceId, error: ERROR_MESSAGES.SOMETHING_WENT_WRONG })));
};

export const cancelFutureRequest = () => (dispatch, getState) => {
  const serviceId = getFTTBServiceId(getState());
  const requestId = getFTTBServiceFutureRequestId(getState());

  dispatch(setFTTBServiceValuesAction({ serviceId, error: null }));
  dispatch(fetchDataError(null));

  cancelFutureTaskRequest({ requestId })
    .then(({ data }) => {
      dispatch(setFTTBServiceValuesAction({
        serviceId,
        isLoading: !!data,
        isDisabled: !!data,
        ...(data ? { requestId: data.requestId } : { error: ERROR_MESSAGES.SOMETHING_WENT_WRONG }),
      }));
    }).catch(() => {
      dispatch(setFTTBServiceValuesAction({ serviceId, error: ERROR_MESSAGES.SOMETHING_WENT_WRONG }));
    });
};

export const getAccessAdditionalService = (serviceId) => (dispatch) => {
  getPopupAdditionalServiceRequest(serviceId)
    .then(({ data }) => data)
    .then((data) => {
      if (!isEmpty(data)) {
        dispatch(fetchServiceAdditionalConnection({ serviceId, data }));
        dispatch(switchServiceAdditionalPopup(true));
        dispatch(changeServiceAdditionalPopupType(SERVICE_CONNECTION_CODES.CONNECTING));
      }
    }).catch((err) => {
      console.error(err);
      dispatch(setFTTBServiceValuesAction({ serviceId, error: ERROR_MESSAGES.SOMETHING_WENT_WRONG }));
    });
};

export const setConnectAdditionalService = (event) => (dispatch, getState) => {
  const serviceId = getFTTBServiceId(getState());
  const { checked: isChecked } = event.target;

  dispatch(setFTTBServiceValuesAction({
    serviceId,
    isLoading: true,
    isDisabled: true,
    ...(!isChecked ? { isDisconnectDialogVisible: !isChecked } : null),
  }));

  if (!isChecked) {
    dispatch(fetchDisconnectionParams());
  } else {
    dispatch(getAccessAdditionalService(serviceId));
  }
};

export const setDisconnectDialogVisible = () => (dispatch, getState) => {
  const serviceId = getFTTBServiceId(getState());

  dispatch(setFTTBServiceValuesAction({
    serviceId,
    isLoading: false,
    isDisabled: false,
    isDisconnectDialogVisible: false,
  }));
};

export const disconnect = () => (dispatch, getState) => {
  const serviceId = getFTTBServiceId(getState());
  const splId = getFTTBServiceSplId(getState());
  const presetId = getFTTBTariffPresetId(getState());
  const disconnectionDate = getFTTBServiceRequestDisconnectionDate(getState());
  const date = isToday(disconnectionDate) ? undefined : disconnectionDate;

  dispatch(setFTTBServiceValuesAction({
    serviceId,
    isLoading: true,
    isDisabled: true,
    isDisconnecting: true,
  }));

  disableServiceRequest({ splId, serviceId: presetId, date })
    .then(({ data }) => data)
    .then(({ isProcessing, requestId }) => {
      if (isProcessing) {
        dispatch(setFTTBServiceValuesAction({
          serviceId,
          error: ERROR_MESSAGES.PREV_REQUEST_IN_PROGRESS_MESSAGE,
        }));
        return;
      }

      dispatch(setFTTBServiceValuesAction({
        serviceId,
        disconnectRequestId: requestId,
        disconnectionDate,
        isChecked: false,
        isDisconnecting: false,
        isDisconnectDialogVisible: false,
      }));
    })
    .catch(() => {
      dispatch(setFTTBServiceValuesAction({
        serviceId,
        isLoading: false,
        isDisabled: false,
        isDisconnecting: false,
        error: ERROR_MESSAGES.SOMETHING_WENT_WRONG,
      }));
    });
};

export const continueServiceAdditionalConnection = () => (dispatch, getState) => {
  const { serviceId, connecting: { email } } = getFTTBAdditionalPopup(getState());

  dispatch(changeServiceAdditionalPopupType(SERVICE_CONNECTION_CODES.LOADING));

  connectAdditionalServiceRequest({ serviceId, ...(email ? { email } : null) })
    .then(({ data }) => data)
    .then((data) => {
      if (isEmpty(data)) {
        dispatch(changeServiceAdditionalPopupType(SERVICE_CONNECTION_CODES.ERROR));
        return;
      }

      dispatch(setServiceAdditionalPopupData(data));
      dispatch(changeServiceAdditionalPopupType(data.code));
    })
    .catch((err) => {
      dispatch(changeServiceAdditionalPopupType(SERVICE_CONNECTION_CODES.ERROR));
      console.error(err);
    });
};

export const requestServiceAdditionalPromisedPayment = () => (dispatch, getState) => {
  const { serviceId } = getFTTBAdditionalPopup(getState());

  dispatch(changeServiceAdditionalPopupType(SERVICE_CONNECTION_CODES.LOADING));

  connectAdditionalServicePromisedPaymentRequest({ serviceId })
    .then(({ data }) => data)
    .then((data) => {
      if (isEmpty(data)) {
        dispatch(changeServiceAdditionalPopupType(SERVICE_CONNECTION_CODES.ERROR));
        return;
      }

      dispatch(setServiceAdditionalPopupData(data));
      dispatch(setFTTBServiceValuesAction({
        serviceId,
        isLoading: data.code === SERVICE_CONNECTION_CODES.SUCCESED,
        isDisabled: data.code === SERVICE_CONNECTION_CODES.SUCCESED,
        ...(data.code === SERVICE_CONNECTION_CODES.SUCCESED ? { futureRequestId: 'fake_id' } : null),
      }));
      dispatch(changeServiceAdditionalPopupType(data.code));
    })
    .catch((err) => {
      dispatch(changeServiceAdditionalPopupType(SERVICE_CONNECTION_CODES.ERROR));
      console.error(err);
    });
};

export const closeServiceAdditionalPopup = () => (dispatch, getState) => {
  const { type, serviceId } = getFTTBAdditionalPopup(getState());

  if (type !== SERVICE_CONNECTION_CODES.SUCCESED) {
    dispatch(setFTTBServiceValuesAction({
      serviceId,
      isLoading: false,
      isDisabled: false,
    }));
  }

  dispatch(switchServiceAdditionalPopup(false));
};

export const onChangeServiceDisconnectionDate = (date) => (dispatch, getState) => {
  const serviceId = getFTTBServiceId(getState());

  dispatch(setFTTBServiceValuesAction({
    serviceId,
    requestDisconnectionDate: date,
  }));
};
